import React, { useState, useEffect } from 'react';
import {
  Rocket, Calendar, DollarSign, TrendingUp, Building2,
  Filter, Search, Loader, AlertCircle, ChevronDown, ChevronUp,
  ExternalLink, Clock, BarChart3, Globe, Tag, ArrowUpRight,
  RefreshCw, Star, Info, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const IPOCalendar = () => {
  const [ipos, setIpos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, upcoming, recent, priced
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date'); // date, name, price
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedIPO, setSelectedIPO] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    fetchIPOCalendar();
  }, []);

  const fetchIPOCalendar = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/market/ipo-calendar`);
      const data = await response.json();
      
      if (data.success && data.data) {
        setIpos(data.data);
      } else if (data.ipos) {
        setIpos(data.ipos);
      } else {
        setIpos(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching IPO calendar:', err);
      setError('Failed to load IPO calendar. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort IPOs
  const processedIPOs = React.useMemo(() => {
    let filtered = [...ipos];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(ipo => 
        ipo.name?.toLowerCase().includes(term) ||
        ipo.symbol?.toLowerCase().includes(term) ||
        ipo.exchange?.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (filter === 'upcoming') {
      filtered = filtered.filter(ipo => {
        const ipoDate = new Date(ipo.ipoDate || ipo.date);
        return ipoDate >= today;
      });
    } else if (filter === 'recent') {
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      filtered = filtered.filter(ipo => {
        const ipoDate = new Date(ipo.ipoDate || ipo.date);
        return ipoDate < today && ipoDate >= thirtyDaysAgo;
      });
    } else if (filter === 'priced') {
      filtered = filtered.filter(ipo => ipo.priceRangeLow || ipo.price);
    }

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'date') {
        comparison = new Date(a.ipoDate || a.date) - new Date(b.ipoDate || b.date);
      } else if (sortBy === 'name') {
        comparison = (a.name || '').localeCompare(b.name || '');
      } else if (sortBy === 'price') {
        comparison = (a.priceRangeLow || 0) - (b.priceRangeLow || 0);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [ipos, searchTerm, filter, sortBy, sortOrder]);

  const toggleRow = (symbol) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(symbol)) {
        newSet.delete(symbol);
      } else {
        newSet.add(symbol);
      }
      return newSet;
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatLargeNumber = (value) => {
    if (!value) return 'N/A';
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return formatCurrency(value);
  };

  const getStatusBadge = (ipo) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ipoDate = new Date(ipo.ipoDate || ipo.date);
    
    if (ipoDate > today) {
      const daysUntil = Math.ceil((ipoDate - today) / (1000 * 60 * 60 * 24));
      return {
        text: daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`,
        color: '#22c55e',
        bg: 'rgba(34, 197, 94, 0.15)'
      };
    } else if (ipoDate.toDateString() === today.toDateString()) {
      return { text: 'TODAY', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' };
    } else {
      return { text: 'Completed', color: '#64748b', bg: 'rgba(100, 116, 139, 0.15)' };
    }
  };

  // Stats calculation
  const stats = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const upcoming = ipos.filter(ipo => new Date(ipo.ipoDate || ipo.date) >= today).length;
    const thisWeek = ipos.filter(ipo => {
      const ipoDate = new Date(ipo.ipoDate || ipo.date);
      const weekFromNow = new Date(today);
      weekFromNow.setDate(weekFromNow.getDate() + 7);
      return ipoDate >= today && ipoDate <= weekFromNow;
    }).length;
    const totalValue = ipos.reduce((sum, ipo) => sum + (ipo.sharesOffered || 0) * (ipo.priceRangeLow || 0), 0);
    
    return { upcoming, thisWeek, totalValue, total: ipos.length };
  }, [ipos]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '4rem',
        minHeight: '400px'
      }}>
        <Loader size={48} style={{ color: '#8b5cf6', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#94a3b8', marginTop: '1rem' }}>Loading IPO Calendar...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%)',
        borderRadius: '1rem',
        padding: '2rem',
        marginBottom: '2rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decoration */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-30%',
          left: '-5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '0.75rem',
              padding: '0.75rem'
            }}>
              <Rocket size={32} color="white" />
            </div>
            <div>
              <h1 style={{ 
                fontSize: '1.75rem', 
                fontWeight: '800', 
                color: 'white',
                margin: 0,
                letterSpacing: '-0.02em'
              }}>
                IPO Calendar
              </h1>
              <p style={{ color: 'rgba(255, 255, 255, 0.8)', margin: 0, fontSize: '0.9rem' }}>
                Track upcoming and recent initial public offerings
              </p>
            </div>
          </div>

          {/* Stats Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '1rem',
            marginTop: '1.5rem'
          }}>
            <StatCard icon={<Calendar size={20} />} label="Upcoming IPOs" value={stats.upcoming} color="#22c55e" />
            <StatCard icon={<Zap size={20} />} label="This Week" value={stats.thisWeek} color="#f59e0b" />
            <StatCard icon={<DollarSign size={20} />} label="Est. Value" value={formatLargeNumber(stats.totalValue)} color="#60a5fa" />
            <StatCard icon={<BarChart3 size={20} />} label="Total Listed" value={stats.total} color="#a78bfa" />
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{
          flex: '1',
          minWidth: '200px',
          maxWidth: '300px',
          position: 'relative'
        }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#64748b'
          }} />
          <input
            type="text"
            placeholder="Search IPOs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 0.75rem 0.75rem 2.5rem',
              background: 'rgba(30, 41, 59, 0.8)',
              border: '1px solid #334155',
              borderRadius: '0.5rem',
              color: '#e2e8f0',
              fontSize: '0.9rem'
            }}
          />
        </div>

        {/* Filter Buttons - Horizontally scrollable on mobile */}
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          flexWrap: 'nowrap',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingBottom: '0.25rem'
        }}>
          <style>{`.filter-scroll::-webkit-scrollbar { display: none; }`}</style>
          {[
            { value: 'all', label: 'All', icon: <Globe size={16} /> },
            { value: 'upcoming', label: 'Upcoming', icon: <Calendar size={16} /> },
            { value: 'recent', label: 'Recent', icon: <Clock size={16} /> },
            { value: 'priced', label: 'Priced', icon: <Tag size={16} /> }
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: filter === f.value 
                  ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' 
                  : 'rgba(30, 41, 59, 0.8)',
                border: filter === f.value ? 'none' : '1px solid #334155',
                borderRadius: '0.5rem',
                color: filter === f.value ? 'white' : '#94a3b8',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: filter === f.value ? '600' : '400',
                transition: 'all 0.2s ease'
              }}
            >
              {f.icon}
              {f.label}
            </button>
          ))}
        </div>

        {/* Refresh Button */}
        <button
          onClick={fetchIPOCalendar}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'rgba(34, 197, 94, 0.15)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '0.5rem',
            color: '#22c55e',
            cursor: 'pointer',
            fontSize: '0.85rem'
          }}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          textAlign: 'center',
          marginBottom: '1.5rem'
        }}>
          <AlertCircle size={32} color="#ef4444" style={{ marginBottom: '0.5rem' }} />
          <p style={{ color: '#f87171', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* IPO Table */}
      {processedIPOs.length > 0 ? (
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          overflow: 'hidden'
        }}>
          {/* Table Header - Hidden on Mobile */}
          <div style={{
            display: 'none',
            gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 80px',
            gap: '1rem',
            padding: '1rem 1.5rem',
            background: 'rgba(15, 23, 42, 0.5)',
            borderBottom: '1px solid #334155',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
          className="ipo-table-header"
          >
            <div 
              onClick={() => { setSortBy('name'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              Company {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
            </div>
            <div 
              onClick={() => { setSortBy('date'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              IPO Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
            </div>
            <div 
              onClick={() => { setSortBy('price'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              Price Range {sortBy === 'price' && (sortOrder === 'asc' ? '↑' : '↓')}
            </div>
            <div>Shares</div>
            <div>Status</div>
            <div></div>
          </div>
          
          {/* Mobile-friendly CSS */}
          <style>{`
            @media (min-width: 768px) {
              .ipo-table-header { display: grid !important; }
              .ipo-table-row { display: grid !important; grid-template-columns: 2fr 1fr 1fr 1fr 1fr 80px !important; }
            }
            @media (max-width: 767px) {
              .ipo-table-row { display: flex !important; flex-direction: column !important; gap: 0.5rem !important; }
              .ipo-mobile-hide { display: none !important; }
            }
          `}</style>

          {/* Table Rows */}
          {processedIPOs.map((ipo, index) => {
            const status = getStatusBadge(ipo);
            const isExpanded = expandedRows.has(ipo.symbol || index);
            
            return (
              <div key={ipo.symbol || index}>
                <div
                  className="ipo-table-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 80px',
                    gap: '1rem',
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid #1e293b',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease'
                  }}
                  onClick={() => toggleRow(ipo.symbol || index)}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(99, 102, 241, 0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  {/* Company */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '0.5rem',
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '0.85rem',
                      color: 'white',
                      flexShrink: 0
                    }}>
                      {(ipo.symbol || ipo.name || '?').substring(0, 2).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: '600', color: '#e2e8f0', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ipo.name || 'Unknown Company'}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        {ipo.symbol || 'N/A'} • {ipo.exchange || 'NASDAQ'}
                      </div>
                      {/* Mobile-only: Show date and price inline */}
                      <div className="mobile-only" style={{ display: 'none', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>📅 {formatDate(ipo.ipoDate || ipo.date)}</span>
                        <span style={{ fontSize: '0.8rem', color: '#22c55e', fontWeight: '600' }}>
                          {ipo.priceRangeLow && ipo.priceRangeHigh ? `$${ipo.priceRangeLow}-$${ipo.priceRangeHigh}` : 'TBD'}
                        </span>
                        <span style={{
                          padding: '0.15rem 0.5rem',
                          borderRadius: '2rem',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          background: status.bg,
                          color: status.color
                        }}>
                          {status.text}
                        </span>
                      </div>
                      <style>{`@media (max-width: 767px) { .mobile-only { display: flex !important; } }`}</style>
                    </div>
                  </div>

                  {/* IPO Date - Hidden on mobile */}
                  <div className="ipo-mobile-hide">
                    <div style={{ fontWeight: '600', color: '#e2e8f0' }}>
                      {formatDate(ipo.ipoDate || ipo.date)}
                    </div>
                  </div>

                  {/* Price Range - Hidden on mobile */}
                  <div className="ipo-mobile-hide">
                    {ipo.priceRangeLow && ipo.priceRangeHigh ? (
                      <div style={{ fontWeight: '600', color: '#22c55e' }}>
                        ${ipo.priceRangeLow} - ${ipo.priceRangeHigh}
                      </div>
                    ) : ipo.price ? (
                      <div style={{ fontWeight: '600', color: '#22c55e' }}>
                        ${ipo.price}
                      </div>
                    ) : (
                      <div style={{ color: '#64748b' }}>TBD</div>
                    )}
                  </div>

                  {/* Shares - Hidden on mobile */}
                  <div className="ipo-mobile-hide" style={{ color: '#94a3b8' }}>
                    {ipo.sharesOffered ? `${(ipo.sharesOffered / 1e6).toFixed(1)}M` : 'TBD'}
                  </div>

                  {/* Status - Hidden on mobile */}
                  <div className="ipo-mobile-hide">
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '2rem',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      background: status.bg,
                      color: status.color,
                      whiteSpace: 'nowrap'
                    }}>
                      {status.text}
                    </span>
                  </div>

                  {/* Expand Button */}
                  <div style={{ textAlign: 'right' }}>
                    {isExpanded ? (
                      <ChevronUp size={20} color="#64748b" />
                    ) : (
                      <ChevronDown size={20} color="#64748b" />
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div style={{
                    padding: '1.5rem',
                    background: 'rgba(15, 23, 42, 0.5)',
                    borderBottom: '1px solid #334155'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '1.5rem'
                    }}>
                      <DetailCard 
                        icon={<Building2 size={18} />}
                        label="Company Details"
                        items={[
                          { label: 'Exchange', value: ipo.exchange || 'NASDAQ' },
                          { label: 'Industry', value: ipo.industry || 'Technology' },
                          { label: 'Country', value: ipo.country || 'USA' }
                        ]}
                      />
                      <DetailCard 
                        icon={<DollarSign size={18} />}
                        label="Offering Details"
                        items={[
                          { label: 'Price Range', value: ipo.priceRangeLow && ipo.priceRangeHigh ? `$${ipo.priceRangeLow} - $${ipo.priceRangeHigh}` : 'TBD' },
                          { label: 'Shares Offered', value: ipo.sharesOffered ? `${(ipo.sharesOffered / 1e6).toFixed(2)}M` : 'TBD' },
                          { label: 'Est. Market Cap', value: formatLargeNumber(ipo.marketCap || (ipo.sharesOffered * (ipo.priceRangeHigh || 0))) }
                        ]}
                      />
                      <DetailCard 
                        icon={<Calendar size={18} />}
                        label="Timeline"
                        items={[
                          { label: 'Filing Date', value: formatDate(ipo.filingDate) },
                          { label: 'IPO Date', value: formatDate(ipo.ipoDate || ipo.date) },
                          { label: 'Pricing Date', value: formatDate(ipo.pricingDate) || 'TBD' }
                        ]}
                      />
                    </div>
                    
                    {ipo.description && (
                      <div style={{ marginTop: '1.5rem' }}>
                        <h4 style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                          <Info size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
                          About
                        </h4>
                        <p style={{ color: '#cbd5e1', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>
                          {ipo.description}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '3rem',
          textAlign: 'center'
        }}>
          <Calendar size={48} color="#64748b" style={{ margin: '0 auto 1rem' }} />
          <h3 style={{ color: '#94a3b8', margin: '0 0 0.5rem' }}>No IPOs Found</h3>
          <p style={{ color: '#64748b', margin: 0 }}>
            {searchTerm 
              ? `No IPOs matching "${searchTerm}"` 
              : 'No IPO data available for the selected filter.'
            }
          </p>
        </div>
      )}

      {/* Educational Section */}
      <div style={{
        marginTop: '2rem',
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
        border: '1px solid rgba(99, 102, 241, 0.2)',
        borderRadius: '0.75rem',
        padding: '1.5rem'
      }}>
        <h3 style={{ 
          color: '#a5b4fc', 
          fontSize: '1rem', 
          fontWeight: '600',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Star size={18} />
          IPO Investing Tips
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem'
        }}>
          <TipCard 
            title="Research the Company"
            description="Read the S-1 filing, understand the business model, and evaluate management before investing."
          />
          <TipCard 
            title="Watch the Lock-up Period"
            description="Insiders can sell after the lock-up expires (typically 90-180 days), which may pressure the stock."
          />
          <TipCard 
            title="Consider Waiting"
            description="Many IPOs drop below their offering price within 6 months. Patience can lead to better entry points."
          />
          <TipCard 
            title="Allocation Matters"
            description="Retail investors often get limited allocation. Hot IPOs may be hard to buy at the offering price."
          />
        </div>
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ icon, label, value, color }) => (
  <div style={{
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    borderRadius: '0.75rem',
    padding: '1rem',
    textAlign: 'center'
  }}>
    <div style={{ color, marginBottom: '0.5rem', display: 'flex', justifyContent: 'center' }}>
      {icon}
    </div>
    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white', marginBottom: '0.25rem' }}>
      {value}
    </div>
    <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.7)' }}>
      {label}
    </div>
  </div>
);

const DetailCard = ({ icon, label, items }) => (
  <div style={{
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid #334155',
    borderRadius: '0.5rem',
    padding: '1rem'
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: '#8b5cf6',
      marginBottom: '0.75rem',
      fontSize: '0.85rem',
      fontWeight: '600'
    }}>
      {icon}
      {label}
    </div>
    {items.map((item, i) => (
      <div key={i} style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '0.5rem 0',
        borderBottom: i < items.length - 1 ? '1px solid #1e293b' : 'none'
      }}>
        <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{item.label}</span>
        <span style={{ color: '#e2e8f0', fontSize: '0.85rem', fontWeight: '500' }}>{item.value}</span>
      </div>
    ))}
  </div>
);

const TipCard = ({ title, description }) => (
  <div style={{
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid #334155',
    borderRadius: '0.5rem',
    padding: '1rem'
  }}>
    <h4 style={{ color: '#e2e8f0', fontSize: '0.9rem', margin: '0 0 0.5rem', fontWeight: '600' }}>
      {title}
    </h4>
    <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0, lineHeight: '1.5' }}>
      {description}
    </p>
  </div>
);

export default IPOCalendar;
